/** Clamps `value` to the range `low`..`high`. */
export function clamp(value: number, low: number, high: number): number {
  return value < low ? low : value > high ? high : value
}

/**
 * Smoothstep: 0 up to `from`, 1 from `to`, and an S-curve between with zero
 * slope at both ends. Pure, and safe to call with `from > to` (it then falls).
 */
export function smoothstep(from: number, to: number, x: number): number {
  const t = clamp((x - from) / (to - from), 0, 1)
  return t * t * (3 - 2 * t)
}

/**
 * Smootherstep (Perlin): as `smoothstep`, but with zero first and second
 * derivative at both ends, so motion starts and stops without a jolt.
 */
export function smootherstep(from: number, to: number, x: number): number {
  const t = clamp((x - from) / (to - from), 0, 1)
  return t * t * t * (t * (t * 6 - 15) + 10)
}

/** How far `x` is through `range` (`[start, end]`), clamped to 0..1, linearly. */
export function rangeProgress(range: readonly [number, number], x: number): number {
  return clamp((x - range[0]) / (range[1] - range[0]), 0, 1)
}

/**
 * A smooth curve through keyframes, sampled at `x`: `times` ascending, with
 * `values` at each. A monotone cubic: flat at the first and last keyframe,
 * and it never overshoots or dips back between two keyframes. Before the first
 * time and after the last it holds the end value.
 */
export function keyCurve(times: readonly number[], values: readonly number[], x: number): number {
  const n = times.length
  if (x <= times[0]) return values[0]
  if (x >= times[n - 1]) return values[n - 1]
  let k = 0
  while (k < n - 2 && x > times[k + 1]) k++
  /** The tangent at keyframe `i`: flat at the ends and at a turning point. */
  function slope(i: number): number {
    if (i === 0 || i === n - 1) return 0
    const before = (values[i] - values[i - 1]) / (times[i] - times[i - 1])
    const after = (values[i + 1] - values[i]) / (times[i + 1] - times[i])
    return before * after <= 0 ? 0 : (2 * before * after) / (before + after)
  }
  const h = times[k + 1] - times[k]
  const u = (x - times[k]) / h
  const m0 = slope(k) * h
  const m1 = slope(k + 1) * h
  const u2 = u * u
  const u3 = u2 * u
  return (
    (2 * u3 - 3 * u2 + 1) * values[k] +
    (u3 - 2 * u2 + u) * m0 +
    (-2 * u3 + 3 * u2) * values[k + 1] +
    (u3 - u2) * m1
  )
}

/** `a` to `b`, `u` of the way (0..1). */
export function lerp(a: number, b: number, u: number): number {
  return a + (b - a) * u
}

/** An angle in radians wrapped to `[-π, π]`. */
export function wrapAng(a: number): number {
  return a - Math.round(a / (2 * Math.PI)) * 2 * Math.PI
}

/** `a` to `b`, `u` of the way (0..1), turning the short way round. Angles in radians. */
export function lerpAng(a: number, b: number, u: number): number {
  return a + wrapAng(b - a) * u
}
